import { useState } from 'react'
import type { Payer } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  ColumnSettingsButton,
  StatusBadge,
  btnPrimary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { PayerEditModal } from './PayerEditModal'
import { useTableColumns } from '../../hooks/use-table-columns'

const PAYER_TYPE_LABELS: Record<string, string> = {
  commercial: 'Commercial',
  medicare: 'Medicare',
  medicaid: 'Medicaid',
  self_pay: 'Self-pay',
  other: 'Other',
}

export function PayerListPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<Payer>({
    path: '/payers',
    filters: { search: debouncedSearch || undefined },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('payers')

  function openEdit(p: Payer) {
    setSelectedId(p.id)
    setIsNew(false)
    setModalOpen(true)
  }
  function openNew() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  const columns: readonly Column<Payer>[] = [
    { key: 'code', header: 'Code', sortable: true, filterable: true, render: (p) => <strong>{p.code}</strong> },
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (p) => p.name },
    { key: 'type', header: 'Type', sortable: true, render: (p) => PAYER_TYPE_LABELS[p.payerType] ?? p.payerType },
    { key: 'edi', header: 'EDI ID', render: (p) => p.ediId || '--' },
    {
      key: 'isSystem',
      header: 'System',
      render: (p) => (p.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    { key: 'status', header: 'Status', sortable: true, render: (p) => (p.isActive ? 'Active' : 'Archived') },
  ]

  return (
    <div>
      <PageHeader
        title="Payers"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNew} style={btnPrimary}>
              Add Payer
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEdit}
            emptyMessage="No payers found. Add one to enable insurance assignment on orders."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
      <PayerEditModal
        payerId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onArchived={() => refresh()}
      />
    </div>
  )
}
